import React, { useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router';
import UserContext from '../../../contexts/UserContext';
import { neighborsLogoPath } from '../../../utils/logoPaths';
import { fetchNeighborsIdentity } from '../../services/Identity';
import { GUEST_HOME_PATH, GUEST_MEMBERSHIP_PATH, GUEST_PENDING_PATH, resolveGuestLandingPath } from '../../utils/guestAccessRouting';
import './style.less';

/*
    Holding page for a guest whose access is not active yet. It explains whichever gate is still
    closed: staff verification first, then membership. The "Thank you" state is the verification
    wait only - once staff verify the guest, the card switches to the membership call to action.
*/
// How often the page re-checks whether staff have approved the guest.
const IDENTITY_RECHECK_MS = 60 * 1000;

const GuestPending = () => {
    const history = useHistory();
    const { userObject, setUser } = useContext(UserContext);
    const userObjectRef = useRef(userObject);
    userObjectRef.current = userObject;
    const access = userObject.guestAccess || {} as Record<string, boolean>;
    const paymentsRequired = Boolean(access.paymentsRequired);
    const verified = Boolean(access.verified);
    const subscriptionActive = Boolean(access.subscriptionActive);

    const membershipOutstanding = paymentsRequired && !subscriptionActive;
    const membershipDone = verified && !membershipOutstanding;

    useEffect(() => {
        // verified with nothing outstanding means access should already be active - this
        // page shouldn't be reachable in that state. Treat it as stale data and send the
        // guest home rather than show a "membership required" CTA with nothing to settle.
        if (membershipDone) {
            history.replace(GUEST_HOME_PATH);
        }
    }, [membershipDone, history]);

    // Staff approval only reaches the app through the identity call, so re-check it while
    // the guest waits here (on focus and every minute) and move them on once it changes.
    useEffect(() => {
        let isMounted = true;
        let isChecking = false;
        const recheckIdentity = async () => {
            if (isChecking) return;
            isChecking = true;
            try {
                const identity = await fetchNeighborsIdentity();
                if (!isMounted || !identity || identity.userType !== 'guest' || !identity.access) return;
                setUser({
                    ...userObjectRef.current,
                    guestAccess: identity.access,
                    guestIdentity: identity,
                    needsOnboarding: identity.needsOnboarding,
                });
                const landingPath = resolveGuestLandingPath(identity.access, identity.needsOnboarding);
                if (landingPath !== GUEST_PENDING_PATH) {
                    history.replace(landingPath);
                }
            } catch (_error) {
                // Keep showing the page; the next check will try again.
            } finally {
                isChecking = false;
            }
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') recheckIdentity();
        };
        const intervalId = window.setInterval(recheckIdentity, IDENTITY_RECHECK_MS);
        window.addEventListener('focus', recheckIdentity);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            isMounted = false;
            window.clearInterval(intervalId);
            window.removeEventListener('focus', recheckIdentity);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [history, setUser]);

    if (membershipDone) {
        return null;
    }

    return (
        <div className="guest-pending-page">
            <main className="guest-pending-card">
                <span className="guest-pending-logo">
                    <img src={`${process.env.PUBLIC_URL}${neighborsLogoPath}`} alt="Speak2 Family" />
                </span>

                {verified ? (
                    <>
                        <div className="guest-pending-mark guest-pending-mark--membership" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
                                <path d="M2.5 10h19" stroke="currentColor" strokeWidth="1.7" />
                                <path d="M6 14.5h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                            </svg>
                        </div>

                        <h1>Membership required</h1>
                        <p className="guest-pending-lead">
                            You&apos;re <b>approved</b> by the community. Start your guest membership to unlock full access.
                        </p>

                        <div className="guest-pending-stack">
                            <button
                                type="button"
                                className="guest-pending-btn"
                                onClick={() => history.push(GUEST_MEMBERSHIP_PATH)}
                            >
                                Manage membership
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="guest-pending-mark" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path className="guest-pending-tick" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>

                        <h1>Thank you!</h1>
                        <p className="guest-pending-lead">
                            Your request has been sent to the community. You&apos;ll receive notice as soon as it&apos;s <b>approved</b>.
                        </p>

                        <div className="guest-pending-stack">
                            <div className="guest-pending-next">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                                    <path d="M12 7v5l3.2 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <p>
                                    <b>Staff verification required</b>
                                    A facility staff member needs to mark your account as verified before full guest access is unlocked.
                                </p>
                            </div>

                            {membershipOutstanding && (
                                <div className="guest-pending-next">
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="3" y="6" width="18" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
                                        <path d="M3 10h18" stroke="currentColor" strokeWidth="1.6" />
                                    </svg>
                                    <p>
                                        <b>Membership required</b>
                                        This facility has credit card payments enabled, so you&apos;ll be asked to start a guest membership once you&apos;re approved.
                                    </p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};

export default GuestPending;
